import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MasterUser, MasterPermission } from '../types/master';
import { MasterRole, getRoleDefinition } from '../constants/masterRoles';
import * as permissions from '../utils/permissions';

export const useMasterAuth = () => {
  const { user, isAuthenticated } = useAuth();
  const [masterUser, setMasterUser] = useState<MasterUser | null>(null);
  const [isMaster, setIsMaster] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isSupport, setIsSupport] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkMasterStatus = async () => {
      if (!isAuthenticated || !user) {
        setMasterUser(null);
        setIsMaster(false);
        setIsPlatformAdmin(false);
        setIsSupport(false);
        setLoading(false);
        return;
      }

      try {
        // Check for master access based on role or isMaster property
        const isMasterUser = user.role === 'master' || 
                            user.isMaster === true ||
                            user.email.includes('master') || 
                            user.email.includes('@eticket.team') ||
                            user.email.includes('@platform.admin');

        if (isMasterUser) {
          // Get the masterRole from user object (from database)
          const masterRole = (user.masterRole as MasterRole) || 'PLATFORM_OWNER';
          const roleDef = getRoleDefinition(masterRole);
          
          // Convert role-based permissions to MasterPermission format
          const rolePermissions: MasterPermission[] = Object.entries(roleDef.permissions)
            .filter(([_, hasPermission]) => hasPermission)
            .map(([permissionName, _]) => ({
              id: permissionName,
              name: permissionName,
              description: `Permission to ${permissionName}`,
              category: getPermissionCategory(permissionName),
              level: getPermissionLevel(permissionName, masterRole)
            }));

          const mockMasterUser: MasterUser = {
            id: user.id,
            email: user.email,
            name: `${user.firstName} ${user.lastName}`,
            role: 'master', // Always master for master dashboard access
            permissions: rolePermissions,
            department: 'management',
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            isActive: true,
          };

          setMasterUser(mockMasterUser);
          setIsMaster(true); // All master users can access master dashboard
          setIsPlatformAdmin(false);
          setIsSupport(false);
        } else {
          setMasterUser(null);
          setIsMaster(false);
          setIsPlatformAdmin(false);
          setIsSupport(false);
        }
      } catch (error) {
        console.error('Error checking master status:', error);
        setMasterUser(null);
        setIsMaster(false);
        setIsPlatformAdmin(false);
        setIsSupport(false);
      } finally {
        setLoading(false);
      }
    };

    checkMasterStatus();
  }, [isAuthenticated, user]);

  // Helper function to categorize permissions
  const getPermissionCategory = (permissionName: string): 'system' | 'support' | 'analytics' | 'accounts' | 'finance' => {
    if (permissionName.includes('Financial') || permissionName.includes('Revenue')) return 'finance';
    if (permissionName.includes('Client') || permissionName.includes('Account')) return 'accounts';
    if (permissionName.includes('Database') || permissionName.includes('System')) return 'system';
    if (permissionName.includes('Analytics') || permissionName.includes('Report')) return 'analytics';
    if (permissionName.includes('Support')) return 'support';
    return 'analytics'; // Default to analytics
  };

  // Helper function to determine permission level
  const getPermissionLevel = (permissionName: string, role: MasterRole): 'read' | 'write' | 'admin' => {
    if (permissionName.includes('manage') || permissionName.includes('create') || permissionName.includes('edit') || permissionName.includes('delete')) {
      return 'write';
    }
    if (permissionName.includes('view') || permissionName.includes('access')) {
      return 'read';
    }
    return role === 'PLATFORM_OWNER' ? 'admin' : 'read';
  };

  const hasPermission = (permissionName: string): boolean => {
    if (!masterUser) return false;
    return masterUser.permissions.some(p => p.name === permissionName);
  };

  const hasAnyPermission = (permissionNames: string[]): boolean => {
    if (!masterUser) return false;
    return permissionNames.some(name => hasPermission(name));
  };

  const canAccessMasterDashboard = (): boolean => {
    return isMaster || isPlatformAdmin || isSupport;
  };

  const canManageAccounts = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canManageClientProfiles(role) || permissions.canManageUserRoles(role);
  };

  const canViewFinancials = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canViewFinancialReports(role);
  };

  const canManageSystem = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canManagePlatformConfig(role);
  };

  const canAccessAnalytics = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canViewAnalytics(role);
  };

  const canManageSupport = (): boolean => {
    // Support tickets management - only platform owners can manage support
    const role = getCurrentMasterRole();
    return role === 'PLATFORM_OWNER';
  };

  const canAccessAuditLogs = (): boolean => {
    return isMaster || hasPermission('view_audit_logs');
  };

  // Get the current user's masterRole
  // NOTE: This intentionally mirrors the broader master-detection used in the
  // useEffect above (role / isMaster flag / email pattern) so that any user the
  // hook treats as "master" also gets a usable MasterRole. Otherwise the
  // permission-gated tabs in the Master Dashboard get filtered out and only
  // the always-visible tabs (Overview, Attendees) appear.
  const getCurrentMasterRole = (): MasterRole | undefined => {
    if (!user) return undefined;

    const isRecognizedMaster =
      user.role === 'master' ||
      user.isMaster === true ||
      user.email.includes('master') ||
      user.email.includes('@eticket.team') ||
      user.email.includes('@platform.admin');

    if (!isRecognizedMaster) return undefined;

    return (user.masterRole as MasterRole) || 'PLATFORM_OWNER';
  };

  const canCreateEvents = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canCreateEvents(role);
  };

  const canEditEvents = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canEditEvents(role);
  };

  const canDeleteEvents = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canDeleteEvents(role);
  };

  const canIssueTickets = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canIssueTickets(role);
  };

  const canAccessDatabase = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canAccessDatabase(role);
  };

  const canManageMasterUsers = (): boolean => {
    const role = getCurrentMasterRole();
    return permissions.canManageUserRoles(role);
  };

  const getDepartmentAccess = (): string[] => {
    if (!masterUser) return [];
    
    const access = [masterUser.department];
    
    if (isMaster) {
      return ['operations', 'finance', 'tech', 'support', 'management'];
    }
    
    return access;
  };

  return {
    masterUser,
    isMaster,
    isPlatformAdmin,
    isSupport,
    loading,
    hasPermission,
    hasAnyPermission,
    canAccessMasterDashboard,
    canManageAccounts,
    canViewFinancials,
    canManageSystem,
    canAccessAnalytics,
    canManageSupport,
    canAccessAuditLogs,
    canCreateEvents,
    canEditEvents,
    canDeleteEvents,
    canIssueTickets,
    canAccessDatabase,
    canManageMasterUsers,
    getCurrentMasterRole,
    getDepartmentAccess,
  };
};

const getMasterPermissions = (role: 'master' | 'platform_admin' | 'support'): MasterPermission[] => {
  const basePermissions: MasterPermission[] = [
    {
      id: 'view_dashboard',
      name: 'view_dashboard',
      description: 'Access master dashboard',
      category: 'analytics',
      level: 'read'
    }
  ];

  const platformAdminPermissions: MasterPermission[] = [
    ...basePermissions,
    {
      id: 'manage_accounts',
      name: 'manage_accounts',
      description: 'Manage client and admin accounts',
      category: 'accounts',
      level: 'write'
    },
    {
      id: 'view_analytics',
      name: 'view_analytics',
      description: 'View platform analytics',
      category: 'analytics',
      level: 'read'
    },
    {
      id: 'view_financials',
      name: 'view_financials',
      description: 'View financial data',
      category: 'finance',
      level: 'read'
    },
    {
      id: 'manage_support',
      name: 'manage_support',
      description: 'Manage support tickets',
      category: 'support',
      level: 'write'
    }
  ];

  const supportPermissions: MasterPermission[] = [
    ...basePermissions,
    {
      id: 'manage_support',
      name: 'manage_support',
      description: 'Manage support tickets',
      category: 'support',
      level: 'write'
    },
    {
      id: 'view_accounts',
      name: 'view_accounts',
      description: 'View account information',
      category: 'accounts',
      level: 'read'
    }
  ];

  const masterPermissions: MasterPermission[] = [
    ...platformAdminPermissions,
    {
      id: 'manage_system',
      name: 'manage_system',
      description: 'Manage system settings and configuration',
      category: 'system',
      level: 'admin'
    },
    {
      id: 'manage_financials',
      name: 'manage_financials',
      description: 'Manage financial settings and payouts',
      category: 'finance',
      level: 'admin'
    },
    {
      id: 'view_audit_logs',
      name: 'view_audit_logs',
      description: 'View audit logs and compliance data',
      category: 'system',
      level: 'read'
    },
    {
      id: 'manage_platform_users',
      name: 'manage_platform_users',
      description: 'Manage platform team users',
      category: 'accounts',
      level: 'admin'
    }
  ];

  switch (role) {
    case 'master':
      return masterPermissions;
    case 'platform_admin':
      return platformAdminPermissions;
    case 'support':
      return supportPermissions;
    default:
      return basePermissions;
  }
}; 